import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Package, 
  Truck, 
  CheckCircle2, 
  Clock, 
  Search,
  ExternalLink,
  ChevronRight,
  XCircle,
  CreditCard,
  ArrowLeft
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderTrackingPage = () => {
  const [searchType, setSearchType] = useState('order_id');
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [order, setOrder] = useState(null);
  const [ordersList, setOrdersList] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchValue.trim()) {
      setError('Please enter an order ID or email address');
      return;
    }

    setLoading(true);
    setError('');
    setOrder(null);
    setOrdersList(null);

    try {
      const payload = searchType === 'order_id' 
        ? { order_id: searchValue.trim() }
        : { email: searchValue.trim() };
      
      const response = await axios.post(`${API}/orders/track`, payload);
      
      if (response.data.orders) {
        setOrdersList(response.data.orders);
      } else {
        setOrder(response.data);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError(searchType === 'order_id' 
          ? 'Order not found. Please check your order ID and try again.'
          : 'No orders found for this email address.');
      } else {
        setError('Something went wrong. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const selectOrder = async (orderId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/orders/track`, { order_id: orderId });
      setOrder(response.data);
      setOrdersList(null);
    } catch (err) {
      setError('Failed to load order details.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-6 h-6 text-yellow-500" />;
      case 'confirmed':
        return <CreditCard className="w-6 h-6 text-blue-500" />;
      case 'shipped':
        return <Truck className="w-6 h-6 text-purple-500" />;
      case 'delivered':
        return <CheckCircle2 className="w-6 h-6 text-green-500" />;
      case 'cancelled':
      case 'refunded':
        return <XCircle className="w-6 h-6 text-red-500" />;
      default:
        return <Package className="w-6 h-6 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'shipped':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled':
      case 'refunded':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Payment Pending';
      case 'confirmed':
        return 'Order Confirmed';
      case 'shipped':
        return 'Shipped';
      case 'delivered':
        return 'Delivered';
      case 'cancelled':
        return 'Cancelled';
      case 'refunded':
        return 'Refunded';
      default:
        return status;
    }
  };

  const getTrackingUrl = (carrier, trackingNumber) => {
    const urls = {
      'USPS': `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`,
      'UPS': `https://www.ups.com/track?tracknum=${trackingNumber}`,
      'FedEx': `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`,
      'DHL': `https://www.dhl.com/en/express/tracking.html?AWB=${trackingNumber}`,
    };
    return urls[carrier?.toUpperCase()] || urls['USPS'];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const resetSearch = () => {
    setOrder(null);
    setOrdersList(null);
    setError('');
    setSearchValue('');
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Header */}
      <div className="bg-[#2A2A2A] text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Store
          </Link>
          <h1 className="text-3xl font-bold tracking-wide">Track Your Order</h1>
          <p className="text-gray-400 mt-2">Enter your order ID or email to check your order status</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Search Form */}
        {!order && !ordersList && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
            <form onSubmit={handleSearch} className="space-y-6">
              {/* Search Type Toggle */}
              <div className="flex gap-4 mb-6">
                <button
                  type="button"
                  onClick={() => { setSearchType('order_id'); setSearchValue(''); setError(''); }}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                    searchType === 'order_id'
                      ? 'bg-[#2A2A2A] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Order ID
                </button>
                <button
                  type="button"
                  onClick={() => { setSearchType('email'); setSearchValue(''); setError(''); }}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
                    searchType === 'email'
                      ? 'bg-[#2A2A2A] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Email Address
                </button>
              </div>

              {/* Search Input */}
              <div>
                <Label htmlFor="search" className="text-gray-700 font-medium">
                  {searchType === 'order_id' ? 'Order ID' : 'Email Address'}
                </Label>
                <div className="relative mt-2">
                  <Input
                    id="search"
                    type={searchType === 'email' ? 'email' : 'text'}
                    placeholder={searchType === 'order_id' ? 'e.g., CB-ABC12345' : 'e.g., you@example.com'}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    className="pl-4 pr-12 py-6 text-lg border-gray-200 rounded-xl focus:ring-[#D4540A] focus:border-[#D4540A]"
                    data-testid="track-search-input"
                  />
                  <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" data-testid="track-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4540A] hover:bg-[#B84709] text-white py-6 text-lg font-semibold rounded-xl"
                data-testid="track-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Searching...
                  </span>
                ) : (
                  'Track Order'
                )}
              </Button>
            </form>

            <p className="text-center text-gray-500 text-sm mt-6">
              Your order ID can be found in your confirmation email
            </p>
          </div>
        )}

        {/* Orders List (when searching by email) */}
        {ordersList && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Your Orders</h2>
                <button
                  onClick={resetSearch}
                  className="text-sm text-[#D4540A] hover:underline"
                >
                  New Search
                </button>
              </div>
              <p className="text-gray-500 mt-1">Found {ordersList.length} order{ordersList.length !== 1 ? 's' : ''}</p>
            </div>
            
            <div className="divide-y divide-gray-100">
              {ordersList.map((o) => (
                <button
                  key={o.order_id}
                  onClick={() => selectOrder(o.order_id)}
                  className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                  data-testid={`order-item-${o.order_id}`}
                >
                  <div className="flex items-center gap-4">
                    {getStatusIcon(o.status)}
                    <div>
                      <p className="font-mono font-bold text-gray-900">{o.order_id}</p>
                      <p className="text-sm text-gray-500">
                        {formatDate(o.created_at)} • {o.item_count} item{o.item_count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(o.status)}`}>
                        {getStatusLabel(o.status)}
                      </span>
                      <p className="font-bold text-gray-900 mt-1">${o.total?.toFixed(2)}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Order Details */}
        {order && (
          <div className="space-y-6" data-testid="order-details">
            {/* Back Button */}
            <button
              onClick={resetSearch}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Search for another order
            </button>

            {/* Status Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Order</p>
                    <p className="font-mono text-2xl font-bold text-gray-900" data-testid="order-id">{order.order_id}</p>
                  </div>
                  <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(order.status)}`} data-testid="order-status">
                    {getStatusIcon(order.status)}
                    {getStatusLabel(order.status)}
                  </span>
                </div>
                <p className="text-gray-500 mt-2">Placed on {formatDate(order.created_at)}</p>
              </div>

              {/* Status Timeline */}
              <div className="p-6 bg-gray-50">
                <div className="flex items-center justify-between">
                  {['pending', 'confirmed', 'shipped', 'delivered'].map((step, index) => {
                    const statusOrder = ['pending', 'confirmed', 'shipped', 'delivered'];
                    const currentIndex = statusOrder.indexOf(order.status);
                    const isCompleted = index <= currentIndex && !['cancelled', 'refunded'].includes(order.status);
                    const isCurrent = order.status === step;
                    
                    return (
                      <React.Fragment key={step}>
                        <div className={`flex flex-col items-center ${isCompleted ? 'text-[#D4540A]' : 'text-gray-300'}`}>
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isCompleted ? 'bg-[#D4540A] text-white' : 'bg-gray-200 text-gray-400'
                          } ${isCurrent ? 'ring-4 ring-[#D4540A]/20' : ''}`}>
                            {step === 'pending' && <Clock className="w-5 h-5" />}
                            {step === 'confirmed' && <CreditCard className="w-5 h-5" />}
                            {step === 'shipped' && <Truck className="w-5 h-5" />}
                            {step === 'delivered' && <CheckCircle2 className="w-5 h-5" />}
                          </div>
                          <span className={`text-xs mt-2 font-medium ${isCompleted ? 'text-gray-700' : 'text-gray-400'}`}>
                            {getStatusLabel(step)}
                          </span>
                        </div>
                        {index < 3 && (
                          <div className={`flex-1 h-1 mx-2 rounded ${
                            index < currentIndex && !['cancelled', 'refunded'].includes(order.status) 
                              ? 'bg-[#D4540A]' 
                              : 'bg-gray-200'
                          }`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* Tracking Info */}
              {order.tracking && order.tracking.tracking_number && (
                <div className="p-6 border-t border-gray-100 bg-green-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-700 font-medium">Tracking Number</p>
                      <p className="font-mono text-lg font-bold text-green-900" data-testid="tracking-number">
                        {order.tracking.tracking_number}
                      </p>
                      <p className="text-sm text-green-600">
                        {order.tracking.carrier} • Shipped {formatDate(order.tracking.shipped_at)}
                      </p>
                    </div>
                    <a
                      href={getTrackingUrl(order.tracking.carrier, order.tracking.tracking_number)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                      data-testid="track-package-btn"
                    >
                      Track Package
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Order Items */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h3 className="font-bold text-gray-900">Items Ordered</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {order.items?.map((item, index) => (
                  <div key={index} className="p-6 flex items-center gap-4" data-testid={`order-item-row-${index}`}>
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-20 h-20 object-contain rounded-lg bg-gray-50"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Package className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.name}</p>
                      <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-bold text-gray-900">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
              
              {/* Order Totals */}
              <div className="p-6 bg-gray-50 border-t border-gray-100">
                <div className="space-y-2">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>${order.totals?.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span>${order.totals?.shipping?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t border-gray-200">
                    <span>Total</span>
                    <span data-testid="order-total">${order.totals?.total?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-bold text-gray-900 mb-3">Shipping To</h3>
              <p className="text-gray-600">
                {order.shipping_address?.name}<br />
                {order.shipping_address?.city}, {order.shipping_address?.state}<br />
                {order.shipping_address?.country}
              </p>
            </div>

            {/* Help Section */}
            <div className="bg-[#FFF9F5] border border-[#D4540A]/20 rounded-2xl p-6">
              <h3 className="font-bold text-[#D4540A] mb-2">Need Help?</h3>
              <p className="text-gray-600 mb-4">
                If you have questions about your order, please contact us at{' '}
                <a href="mailto:support@countrybofoods.com" className="text-[#D4540A] font-medium hover:underline">
                  support@countrybofoods.com
                </a>
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-[#D4540A] font-medium hover:underline"
              >
                Contact Support
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTrackingPage;
